import React, { useState, useEffect, useMemo, useRef } from "react";
import useDebouncer from "common/hooks/debouncer";
import styled from "styled-components";
import * as d3 from "d3";
import NVLineChart, {
  defaultMargin as lineChartDefaultMargin,
} from "common/graphs/NVLineChart";
import NVStackedAreaChart, {
  defaultMargin as stackedAreaChartDefaultMargin,
} from "common/graphs/NVStackedAreaChart";

const sumOfValuesGiven = (keys) => (item) =>
  d3.sum(keys, (key) => item[key] ?? 0);

const pickMostRelevant = (bucket, keys) =>
  bucket[d3.maxIndex(bucket, sumOfValuesGiven(keys))];

const maySquashIntoItem = (squash) => (bucket, keys) =>
  bucket.length < 2
    ? bucket[0]
    : {
        ...squash(bucket, keys),
        time: bucket[0].time,
      };

export const mostRelevant = maySquashIntoItem(pickMostRelevant);

const calcMaxOfEachAttribute = (bucket, keys) => {
  const result = Object.create(null);
  for (let key of keys) {
    result[key] = d3.max(bucket, (item) => item[key]);
  }
  return result;
};

export const maxOfEachAttribute = maySquashIntoItem(calcMaxOfEachAttribute);

const statistics = (keys, {min: givenMin=undefined, max: givenMax=undefined}={}) => {
  let isZeroOrNull = true;
  let min = givenMin;
  let max = givenMax;

  const mind = (item) => {
    for (const key of keys) {
      const value = item[key];
      if (value === null || value === undefined) {
        continue;
      }
      if (value > 0) {
        isZeroOrNull = false;
      }
      if (min === undefined || value < min) {
        min = value;
      }
      if (max === undefined || value > max) {
        max = value;
      }
    }
    return item;
  };

  const result = () => {
    const limits = min === undefined && max === undefined ? [] : [min, max];
    return { isZeroOrNull, limits };
  };

  return { mind, result };
};

const statsFromItems = (items, keys) => {
  const stats = statistics(keys);
  items.forEach(stats.mind);
  return stats.result();
};

export const downsampling = ({
  data,
  keys = [],
  sampleSize,
  squash = maxOfEachAttribute,
}) => {
  let result = [];
  let bucket = [];
  const stats = statistics(keys);
  for (let i = 0; i < data.length; i += 1) {
    if (i > 0 && i % sampleSize === 0) {
      result.push(stats.mind(squash(bucket, keys)));
      bucket = [{ ...data[i] }];
    } else {
      bucket.push({ ...data[i] });
    }
  }
  if (bucket.length > 0) {
    result.push(stats.mind(squash(bucket, keys)));
  }
  return { items: result, ...stats.result() };
};

const isInsideRange =
  ({ from, to }) =>
  ({ time }) =>
    from <= time && time <= to;

const adaptToRange = (range, items, {copyEndpoints=false}={}) => [
  ...(items.at(0).time <= range.from
    ? []
    : [{ ...(copyEndpoints ? items.at(0) : {}), time: range.from }]),
  ...items.filter(isInsideRange(range)),
  ...(items.at(-1).time >= range.to
    ? []
    : [{ ...(copyEndpoints ? items.at(-1) : {}), time: range.to }]),
];

const applyRange = (range, items, options) =>
  !range || items.length === 0 ? items : adaptToRange(range, items, options);

const mayDownsample = (items, keys, threshold, squash) =>
  threshold > 0 && items.length > threshold
    ? downsampling({
        data: items,
        keys,
        sampleSize: Math.ceil(items.length / threshold),
        squash,
      })
    : { items, ...statsFromItems(items, keys) };

const Container = styled.div`
  width: 100%;
  flex: 1;
  border: 0px;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-flow: row nowrap;
`;

const defaultMargin = { top: 5, right: 50, bottom: 30, left: 70 };

const DownSampling = ({
  Chart,
  items = [],
  range,
  fields = [],
  ratio = 2,
  margin = defaultMargin,
  requiresZeroOrNull = false,
  copyEndpoints = false,
  squash = maxOfEachAttribute,
  ...rest
}) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const obtainedWidthRef = useRef(0);
  const debounce = useDebouncer(200);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (entries && entries[0]) {
        const { width: entryWidth } = entries[0].contentRect;
        const newWidth = entryWidth - margin.left - margin.right;
        if (obtainedWidthRef.current === 0) {
          obtainedWidthRef.current = newWidth;
          setWidth(newWidth);
        } else {
          debounce(() => setWidth((obtainedWidthRef.current = newWidth)));
        }
      }
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      debounce.stop();
    };
  }, [margin.left, margin.right]);

  const threshold = width > 0 ? Math.floor(width / ratio) : 0;

  const keys = useMemo(
    () => fields.map((f) => (typeof f === "string" ? f : f.name)),
    [fields]
  );
  // The callers often build their fields while rendering, so the keys take
  // part in the sampling by their content: a new list of the same names is
  // the same work, and doing it again would draw the same chart. The
  // separator is one no name carries, so that no two lists look alike.
  const keysGiven = keys.join("\u0000");

  const rangedItems = useMemo(
    () => applyRange(range, items, { copyEndpoints }),
    [range, items, copyEndpoints]
  );

  // Sampling walks every item of every key, and both this and the range
  // above copy the whole series, so they are done again only when the items,
  // the keys or the number of samples that fit in the width change. Their
  // results keep their identity in between, which is what the charts compare
  // to decide whether they have to be drawn again.
  const {
    items: downsampledItems,
    isZeroOrNull: sampledIsZeroOrNull,
    limits,
  } = useMemo(
    () => mayDownsample(rangedItems, keys, threshold, squash),
    [rangedItems, keysGiven, threshold, squash]
  );

  const isZeroOrNull = requiresZeroOrNull ? sampledIsZeroOrNull : undefined;

  return (
    <Container ref={containerRef}>
      {width > 0 ? (
        <Chart
          items={downsampledItems}
          margin={margin}
          fields={fields}
          isZeroOrNull={isZeroOrNull}
          limits={limits}
          fixedYRange={
            isZeroOrNull !== undefined
              ? isZeroOrNull
                ? [0, 1]
                : [0, null]
              : undefined
          }
          {...rest}
        />
      ) : null}
    </Container>
  );
};

export const NVLineChartDownsampled = ({
  margin = lineChartDefaultMargin,
  ...props
}) => <DownSampling Chart={NVLineChart} margin={margin} {...props} />;

export const NVStackedAreaChartDownsampled = ({
  margin = stackedAreaChartDefaultMargin,
  ...props
}) => (
  <DownSampling
    Chart={NVStackedAreaChart}
    squash={mostRelevant}
    margin={margin}
    {...props}
  />
);

export default DownSampling;
